import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AuthorizationGuard } from './guards/authorization.guard';
import { PublicComponent } from './layout/public/public.component';
import { PrivateComponent } from './layout/private/private.component';
import { SharedModule } from './shared/';

const routes: Routes = [
  {
    path: '',
    component: PrivateComponent,
    children: [
      {
        path: '',
        redirectTo: 'contents',
        pathMatch: 'full',
        canActivate: [AuthorizationGuard]
      },
      {
        path: 'assistance',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/assistance/assistance.module').then(m => m.AssistanceModule)
      },
      {
        path: 'settings',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/settings/settings.module').then(m => m.SettingsModule)
      },
      {
        path: 'last-pages-modified',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/dashboard/dashboard.module').then(m => m.DashboardModule)
      },
      {
        path: 'profile',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/profile/profile.module').then(m => m.ProfileModule)
      },
      {
        path: 'articles',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/articles/articles.module').then(m => m.ArticlesModule)
      },
      {
        path: 'assets',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/assets/assets.module').then(m => m.AssetsModule)
      },
      {
        path: 'pages',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/pages/pages.module').then(m => m.PagesModule)
      },
      {
        path: 'page/:code',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/page/page.module').then(m => m.PageModule),
        data: { state: true }
      },
      {
        path: 'page/:name/:code',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/advanced-page/advanced-page.module').then(m => m.AdvancedPageModule),
        data: { state: true }
      },
      {
        path: 'files',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/files/files.module').then(m => m.FilesModule)
      },
      {
        path: 'banners',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/banners/banners.module').then(m => m.BannersModule)
      },
      {
        path: 'forms',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/forms/forms.module').then(m => m.AppFormsModule)
      },
      {
        path: 'schemas',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/schemas/schemas.module').then(m => m.SchemasModule)
      },
      {
        path: 'contents',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/contents/contents.module').then(m => m.ContentsModule)
      },
      {
        path: 'layouts',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/layouts/layouts.module').then(m => m.LayoutsModule)
      },
      {
        path: 'layouts/:code',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/layouts/layouts.module').then(m => m.LayoutsModule)
      },
      {
        path: 'resource',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/resources/resource.module').then(m => m.ResourceModule)
      },
      {
        path: 'app-resource/:code',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/resources/resource.module').then(m => m.ResourceModule)
      },
      {
        path: 'sections',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/sections/sections.module').then(m => m.SectionsModule)
      },
      {
        path: 'sections/:code',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/sections/sections.module').then(m => m.SectionsModule)
      },
      {
        path: 'scripts',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/scripts/scripts.module').then(m => m.ScriptsModule)
      },
      {
        path: 'scripts/:code',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/scripts/scripts.module').then(m => m.ScriptsModule)
      },
      {
        path: 'styles',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/styles/styles.module').then(m => m.StylesModule)
      },
      {
        path: 'styles/:code',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/styles/styles.module').then(m => m.StylesModule)
      },
      {
        path: 'templates',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/templates/templates.module').then(m => m.TemplatesModule)
      },
      {
        path: 'templates/:code',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/templates/templates.module').then(m => m.TemplatesModule)
      },
      {
        path: 'users',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/users/users.module').then(m => m.UsersModule)
      },
      {
        path: 'change-password',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/change-password/change-password.module').then(m => m.ChangePasswordModule)
      },
      {
        path: 'redirection',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/redirection/redirection.module').then(m => m.RedirectionModule)
      },
      {
        path: 'redirection/:code',
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./pages/redirection/redirection.module').then(m => m.RedirectionModule)
      }
    ]
  },
  {
    path: '',
    component: PublicComponent,
    children: [
      {
        path: 'login',
        loadChildren: () => import('./pages/login/login.module').then(m => m.LoginModule),
        pathMatch: 'full'
      },
      {
        path: 'logout',
        loadChildren: () => import('./pages/logout/logout.module').then(m => m.LogoutModule),
        pathMatch: 'full'
      },
      {
        path: 'register',
        loadChildren: () => import('./pages/register/register.module').then(m => m.RegisterModule),
        pathMatch: 'full'
      },
      {
        path: 'forgot-password',
        loadChildren: () => import('./pages/forgot-password/forgot-password.module').then(m => m.ForgotPasswordModule),
        pathMatch: 'full'
      },
      {
        path: 'reset-password',
        loadChildren: () => import('./pages/reset-password/reset-password.module').then(m => m.ResetPasswordModule),
        pathMatch: 'full'
      },
      {
        path: 'help',
        loadChildren: () => import('./pages/help/help.module').then(m => m.HelpModule),
        pathMatch: 'full'
      }
    ]
  }
];

@NgModule(
  {
    declarations: [
      PrivateComponent,
      PublicComponent
    ],
    entryComponents: [],
    imports: [
      CommonModule,
      FormsModule,
      ReactiveFormsModule,
      SharedModule,
      RouterModule.forRoot(routes)
    ],
    exports: [RouterModule]
  })
export class AppRoutingModule { }
